// src/gestion-proforma/GestionProforma.tsx
import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  Layout,
  Button,
  Input,
  Select,
  DatePicker,
  Table,
  Modal,
  Form,
  InputNumber,
  Spin,
  message,
  Row,
  Col,
  Space,
  Tag,
  Tabs,
  AutoComplete,
  Card,
  Descriptions,
  Alert,
  Empty,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  QrcodeOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  SwapOutlined,
  UndoOutlined,
  PictureOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTaxes } from "../gestion-vente/useTaxes";
import { useSelection } from "../SelectionContext";
import { debounce } from "lodash";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
// Importations supplémentaires pour la génération PDF
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { generateProformaHTML } from "./ProformaHTMLTemplate";
// Ajouter l'import de html2pdf en haut du fichier si non présent
import html2pdf from "html2pdf.js";
import { useAuth } from "../context/AuthContext"; // Import useAuth hook
import type { TableProps } from "antd"; // Added import for TableProps
import API_CONFIG from "../config/api.js";
import RowActionsMenu from "../components/RowActionsMenu";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateBusinessResources } from "../utils/businessResourceFreshness";
import OrderProductSearch from "../components/orders/OrderProductSearch";
import { saleFormData } from "../components/orders/orderFormData";
import {
  CommercialDocumentAdditionalInfo,
  CommercialDocumentModalTitle,
  CommercialDocumentSummary,
} from "../components/orders/CommercialDocumentShell";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Utilitaires et constantes
const formatNumber = (value: number): string => {
  return Number(value).toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

interface Product {
  id_produit: number;
  item_type?: "product" | "service";
  product_id?: number | null;
  service_id?: number | null;
  unit_id?: number | null;
  nom_produit: string;
  prix_vente: number;
  quantite_stock?: number;
  code_barre?: string;
}

interface ProduitVendu {
  produit_id: number;
  item_type?: "product" | "service";
  service_id?: number | null;
  nom_produit: string;
  quantite: number;
  prix_unitaire_HT: number;
  remise?: number;
  taxe?: number | null;
  montant_taxe?: number;
  quantite_stock?: number;
  product_id?: number | null;
  product_name?: string;
  quantity?: number;
  unit_price?: number;
  discount_rate?: number;
  tax_id?: number;
  total_tax?: number;
  subtotal?: number;
  unit_id?: number | null;
}

interface ProformaFormData {
  id?: number;
  invoice_number?: string;
  Date_Facture: string;
  Statut_Vente: string;
  Client_ID: string;
  produitsVendus: ProduitVendu[];
  remise_globale: number;
  termes_conditions?: string;
  remarques?: string;
}

interface DateRange {
  start: string;
  end: string;
}

interface ProformasState {
  proformas: any[];
  clients: any[];
  produits: Product[];
  taxes: any[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  searchTerm: string;
  dateRange: DateRange;
  selectedClient: any | null;
  showForm: boolean;
  formData: ProformaFormData;
  showDeleted: boolean;
  activeTab: string;
}

type ProformasAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_PROFORMAS"; payload: { proformas: any[]; total: number } }
  | { type: "DELETE_PROFORMA"; payload: number }
  | { type: "SET_CLIENTS"; payload: any[] }
  | { type: "SET_PRODUITS"; payload: Product[] }
  | { type: "SET_TAXES"; payload: any[] }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_DATE_RANGE"; payload: DateRange }
  | { type: "SET_SELECTED_CLIENT"; payload: any }
  | { type: "TOGGLE_FORM"; payload: boolean }
  | { type: "SET_FORM_DATA"; payload: ProformaFormData }
  | { type: "UPDATE_FORM_DATA"; payload: ProformaFormData }
  | { type: "TOGGLE_SHOW_DELETED"; payload: boolean }
  | { type: "SET_ACTIVE_TAB"; payload: string }
  | { type: "SET_ITEMS_PER_PAGE"; payload: number }
  | { type: "RESET_FORM_DATA" }
  | {
      type: "UPDATE_PROFORMA_DETAIL";
      payload: { id: number; produitsVendus: ProduitVendu[] };
    };

const sectionStyle = {
  border: "1px solid #ccc",
  padding: "16px",
  borderRadius: "4px",
  marginBottom: "16px",
};

const BASE_URL = API_CONFIG.BASE_URL;

// ✅ FIX: Helper function to get authentication headers (pattern from GestionVente)
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// Exporté pour l'écran mobile (src/mobile/ventes/ProformaMobile.jsx).
export const api = {
  async fetchProformas(params: any) {
    try {
      // Construire les paramètres de requête
      const queryParams = new URLSearchParams();
      queryParams.append("order_type", "proforma");
      queryParams.append("page", params.page || "1");
      queryParams.append("limit", params.limit || "10");

      const search = String(params.search || "").trim();
      if (search) queryParams.append("search", search);
      if (params.company_id) queryParams.append("company_id", String(params.company_id));

      if (params.client_id) {
        queryParams.append("user_id", params.client_id);
      }

      if (params.start_date && params.end_date) {
        queryParams.append("start_date", params.start_date);
        queryParams.append("end_date", params.end_date);
      }

      if (params.warehouse_id) {
        queryParams.append("warehouse", params.warehouse_id);
      }

      if (params.show_deleted) {
        queryParams.append("deleted", "1");
      }

      if (params.status) {
        queryParams.append("status", params.status);
      }

      // Effectuer la requête
      const url = `${BASE_URL}/api/orders?${queryParams.toString()}`;

      // Log supplémentaire pour vérifier l'ID du magasin

      const response = await fetch(url, {
        headers: {
          ...getAuthHeaders(),
          ...(params.company_id ? { "X-Company-Id": String(params.company_id) } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const data = await response.json();

      // Vérifier si nous avons bien reçu l'objet attendu
      if (!data || !data.orders || !Array.isArray(data.orders)) {
        console.error("Format de réponse inattendu:", data);
        throw new Error("Format de réponse inattendu");
      }

      // Vérifier que toutes les proformas appartiennent bien au magasin sélectionné
      const proformasFromOtherWarehouses = data.orders.filter(
        (proforma: any) =>
          proforma.warehouse_id && proforma.warehouse_id !== params.warehouse_id
      );

      if (proformasFromOtherWarehouses.length > 0) {
        console.warn(
          "⚠️ Des proformas d'autres établissements ont été récupérées:",
          proformasFromOtherWarehouses
        );
      } else {
      }

      // Traiter les données reçues pour identifier les proformas converties
      data.orders.forEach((proforma: any) => {
        // Vérifier si cette proforma a déjà été convertie
        if (proforma.converted_to_sale || proforma.sale_id) {
        }
      });

      return {
        proformas: data.orders,
        total: data.total ?? data.pagination?.total ?? data.orders.length,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des proformas:", error);
      throw error;
    }
  },

  async fetchClients(companyId: number | string | null, warehouseId: number | string | null) {
    try {
      let url = `${BASE_URL}/api/users/customers`;
      const params = new URLSearchParams();

      if (companyId) {
        params.append('companyId', String(companyId));
      }

      if (warehouseId) {
        params.append('warehouseId', String(warehouseId));
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        console.error(
          `Erreur lors de la récupération des clients: ${response.status}`
        );
        throw new Error(
          `Erreur lors de la récupération des clients: ${response.status}`
        );
      }
      const data = await response.json();

      // Dédoublonnage des clients (correction des doublons en BDD)
      const { deduplicateEntities, countDuplicates } = await import('../utils/entityUtils');
      const uniqueClients = deduplicateEntities(data);

      // Log pour monitoring (seulement si des doublons détectés)
      const stats = countDuplicates(data);
      if (stats.duplicates > 0) {
        console.warn(`[fetchClients] Doublons détectés et corrigés: ${stats.duplicates} sur ${stats.total} entrées`);
      }

      return uniqueClients;
    } catch (error) {
      console.error("Erreur lors de la récupération des clients:", error);
      throw error;
    }
  },

  async fetchProduits(params: any = {}) {
    // Construction dynamique des paramètres de la requête
    const queryParams: any = {};

    if (params.search) queryParams.search = params.search;
    if (params.limit) queryParams.limit = params.limit;
    if (params.page) queryParams.page = params.page;
    if (params.warehouse) {
      queryParams.warehouse = params.warehouse;
    }
    if (params.barcode) queryParams.barcode = params.barcode;
    if (params.categorie) queryParams.categorie = params.categorie;

    const queryString = new URLSearchParams(queryParams).toString();
    try {
      const response = await fetch(`${BASE_URL}/api/produits?${queryString}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Erreur lors de la récupération des produits:", error);
      throw error;
    }
  },

  async fetchTaxes() {
    try {
      const response = await fetch(`${BASE_URL}/api/taxes`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();
      return data.taxes;
    } catch (error) {
      console.error("Erreur lors de la récupération des taxes:", error);
      throw error;
    }
  },

  async createProforma(data: any) {
    try {
      // Générer un numéro de proforma personnalisé
      // Format: PRF-{YEAR}{MONTH}-{SEQUENTIAL_NUMBER}
      const currentDate = new Date();
      const year = currentDate.getFullYear().toString().substr(-2); // Derniers 2 chiffres de l'année
      const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Mois sur 2 chiffres

      // Récupérer le dernier numéro de proforma pour incrémenter le compteur
      const lastProformas = await this.fetchProformas({
        limit: 1,
        order_type: "proforma",
        invoice_type: "standard",
      });

      let sequentialNumber = 1;
      if (
        lastProformas &&
        lastProformas.proformas &&
        lastProformas.proformas.length > 0
      ) {
        // Chercher d'abord un numéro commençant par PRF-
        const lastProformaWithPRF = lastProformas.proformas.find(
          (p: any) => p.invoice_number && p.invoice_number.startsWith("PRF-")
        );

        if (lastProformaWithPRF) {
          const match =
            lastProformaWithPRF.invoice_number.match(/PRF-\d{4}-(\d+)/);
          if (match && match[1]) {
            sequentialNumber = parseInt(match[1], 10) + 1;
          }
        } else {
          // Si aucun numéro ne commence par PRF-, prendre le dernier numéro de facture et ajouter 1
          sequentialNumber = lastProformas.proformas.length + 1;
        }
      }

      // Formater le numéro séquentiel avec des zéros à gauche (ex: 001, 010, 100)
      const formattedSequentialNumber = String(sequentialNumber).padStart(
        3,
        "0"
      );

      // Construire le numéro final de proforma
      const proformaNumber = `PRF-${year}${month}-${formattedSequentialNumber}`;

      // Convertir les données au format attendu par l'API de commandes
      const orderData = {
        company_id: data.company_id, // Utiliser selectedCompany du contexte (fourni ligne 985)
        warehouse_id: data.warehouse_id,
        order_date: data.Date_Facture,
        order_type: "proforma", // Type de commande (maintenant utilisé pour identifier les proformas)
        invoice_type: "standard", // Type de facture (maintenant standard pour les proformas)
        invoice_number: proformaNumber, // Utiliser notre numéro personnalisé
        user_id: Number(data.Client_ID), // Forcer la conversion en nombre
        discount: data.remise_globale || 0,
        notes: data.remarques || "",
        terms_condition: data.termes_conditions || "",
        order_status: data.Statut_Vente || "Commandé",
        payment_status: "Non applicable", // Pas de paiement pour les proformas
        tax_amount: data.Montant_Taxe || 0,
        tax_rate: 0,
        shipping: 0,
        subtotal: data.Montant_Total_HT || 0,
        total: data.Montant_TTC || 0,
        paid_amount: 0,
        due_amount: 0, // Pas de montant dû pour les proformas
        total_items: data.produitsVendus?.length || 0,
        total_quantity:
          data.produitsVendus?.reduce(
            (sum: number, p: ProduitVendu) => sum + p.quantite,
            0
          ) || 0,
        items: (data.produitsVendus || []).map((p: ProduitVendu) => ({
          item_type: p.item_type || "product",
          product_id: p.item_type === "service" ? null : p.product_id || p.produit_id,
          service_id: p.item_type === "service" ? p.service_id || Math.abs(p.produit_id) : null,
          unit_id: p.item_type === "service" ? null : p.unit_id || 1,
          quantity: p.quantite,
          unit_price: p.prix_unitaire_HT,
          discount_rate: p.remise || 0,
          tax_id: p.taxe || null,
          tax_rate: 0,
          tax_type: "amount",
          total_tax: p.montant_taxe || 0,
          total_discount: p.remise || 0,
          subtotal: p.prix_unitaire_HT * p.quantite,
        })),
      };

      // Appel API pour créer la proforma
      const response = await fetch(`${BASE_URL}/api/orders`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur lors de la création: ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Erreur lors de la création de la proforma:", error);
      throw error;
    }
  },

  async updateProforma(id: number, data: any) {
    try {
      // Récupérer les détails de la proforma existante pour obtenir le numéro de facture
      const existingProforma = await this.fetchProformaDetail(id.toString());

      // Convertir les données au format attendu par l'API de commandes
      const orderData = {
        company_id: data.company_id, // Utiliser selectedCompany du contexte (fourni ligne 985)
        warehouse_id: data.warehouse_id,
        order_date: data.Date_Facture,
        order_type: "proforma", // Type de commande maintenant utilisé pour identifier les proformas
        invoice_type: "standard", // Type de facture maintenant standard pour les proformas
        invoice_number: existingProforma.invoice_number, // Conserver le numéro existant
        user_id: Number(data.Client_ID),
        discount: data.remise_globale || 0,
        notes: data.remarques || "",
        terms_condition: data.termes_conditions || "",
        order_status: data.Statut_Vente || "Commandé",
        payment_status: "Non applicable", // Pas de paiement pour les proformas
        tax_amount: data.Montant_Taxe || 0,
        tax_rate: 0,
        shipping: 0,
        subtotal: data.Montant_Total_HT || 0,
        total: data.Montant_TTC || 0,
        total_items: data.produitsVendus?.length || 0,
        total_quantity:
          data.produitsVendus?.reduce(
            (sum: number, p: ProduitVendu) => sum + p.quantite,
            0
          ) || 0,
        items: (data.produitsVendus || []).map((p: ProduitVendu) => ({
          item_type: p.item_type || "product",
          product_id: p.item_type === "service" ? null : p.product_id || p.produit_id,
          service_id: p.item_type === "service" ? p.service_id || Math.abs(p.produit_id) : null,
          unit_id: p.item_type === "service" ? null : p.unit_id || 1,
          quantity: p.quantite,
          unit_price: p.prix_unitaire_HT,
          discount_rate: p.remise || 0,
          tax_id: p.taxe || null,
          tax_rate: 0,
          tax_type: "amount",
          total_tax: p.montant_taxe || 0,
          total_discount: p.remise || 0,
          subtotal: p.prix_unitaire_HT * p.quantite,
        })),
      };

      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur lors de la mise à jour: ${errorText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la proforma:", error);
      throw error;
    }
  },

  async deleteProforma(id: number) {
    try {
      // Modification de la méthode de PATCH à DELETE et de l'URL
      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Erreur lors de la suppression de la proforma:", error);
      throw error;
    }
  },

  async fetchProformaDetail(id: string) {
    try {
      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();

      // Vérifier que c'est bien une proforma
      if (data && data.order_type !== "proforma") {
        console.error("La commande récupérée n'est pas une proforma:", data);
        throw new Error("La commande récupérée n'est pas une proforma");
      }

      return data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération du détail de la proforma:",
        error
      );
      throw error;
    }
  },

  async fetchOrderItems(orderId: string) {
    try {
      // Récupérer les éléments de la commande
      const response = await fetch(`${BASE_URL}/api/orders/${orderId}/items`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const items = await response.json();

      // Enrichir les produits avec leurs noms si nécessaire
      if (items && items.length > 0) {
        try {
          // Extraire les IDs des produits
          const productIds = items
            .map((item: any) => item.product_id)
            .filter(Boolean);

          if (productIds.length > 0) {
            // Récupérer les détails des produits
            const productResponse = await fetch(
              `${BASE_URL}/api/produits/by-ids?ids=${productIds.join(",")}`,
              {
                headers: getAuthHeaders(),
              }
            );
            if (productResponse.ok) {
              const productData = await productResponse.json();

              if (
                productData &&
                productData.products &&
                Array.isArray(productData.products)
              ) {
                // Créer un dictionnaire pour un accès rapide aux détails des produits
                const productDetails: { [key: number]: any } = {};
                productData.products.forEach((product: any) => {
                  productDetails[product.id] = product;
                });

                // Enrichir chaque élément avec le nom du produit
                items.forEach((item: any) => {
                  if (item.product_id && productDetails[item.product_id]) {
                    item.product_name =
                      productDetails[item.product_id].name ||
                      `Produit #${item.product_id}`;
                  }
                });
              }
            }
          }
        } catch (error) {
          console.error("Erreur lors de l'enrichissement des produits:", error);
          // Continue avec les données non-enrichies
        }
      }

      return items;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails de la commande:",
        error
      );
      throw error;
    }
  },

  async fetchWarehouses() {
    try {
      const response = await fetch(`${BASE_URL}/api/warehouses`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();
      return data.warehouses;
    } catch (error) {
      console.error("Erreur lors de la récupération des établissements:", error);
      throw error;
    }
  },

  async restoreProforma(id: number) {
    try {
      const response = await fetch(`${BASE_URL}/api/orders/${id}/restore`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Erreur lors de la restauration de la proforma:", error);
      throw error;
    }
  },

  async cancelProforma(id: number) {
    try {
      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Erreur lors de l'annulation de la proforma:", error);
      throw error;
    }
  },

  async fetchWarehouse(id: number) {
    try {
      const response = await fetch(`${BASE_URL}/api/warehouses/${id}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération de l'établissement ID=${id}:`,
        error
      );
      throw error;
    }
  },

  async convertToSale(id: number, companyId: number) {
    try {
      const scopedCompanyId = Number(companyId);
      if (!Number.isInteger(scopedCompanyId) || scopedCompanyId <= 0) {
        throw new Error("Sélectionnez l'entreprise de la proforma avant de la convertir.");
      }

      const response = await fetch(
        `${BASE_URL}/api/orders/${id}/convert-to-sale`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
            "X-Company-Id": String(scopedCompanyId),
          },
          body: JSON.stringify({ company_id: scopedCompanyId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          (typeof errorData?.message === "string" && errorData.message) ||
          (typeof errorData?.error === "string" && errorData.error) ||
          "Erreur lors de la conversion de la proforma en vente.";
        throw new Error(errorMessage);
      }

      const result = await response.json();

      return {
        id: result.saleId,
        orderId: result.saleId,
        invoice_number: result.invoice_number,
        verification_token: result.verification_token,
        message: result.message,
      };
    } catch (error) {
      console.error(
        "Erreur lors de la conversion de proforma en vente:",
        error
      );
      throw error;
    }
  },

  async fetchProductDetails(productIds: number[]) {
    try {
      if (!productIds || productIds.length === 0) {
        return [];
      }

      // Convertir le tableau d'IDs en chaîne pour l'URL
      const idsParam = productIds.join(",");
      const response = await fetch(`${BASE_URL}/api/produits/by-ids?ids=${idsParam}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();

      if (!data || !data.products || !Array.isArray(data.products)) {
        console.error("Format de réponse invalide pour les produits:", data);
        return [];
      }

      // Enrichir les produits avec les attributs nécessaires pour notre interface
      const enrichedProducts = data.products.map((product: any) => ({
        ...product,
        id_produit: product.id, // Assurer la compatibilité avec notre interface
        nom_produit: product.name || `Produit #${product.id}`, // Assurer que le nom est toujours disponible
      }));

      return enrichedProducts;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des détails des produits:",
        error
      );
      return [];
    }
  },
};

const initialState: ProformasState = {
  proformas: [],
  clients: [],
  produits: [],
  taxes: [],
  loading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 10,
  totalPages: 0,
  searchTerm: "",
  dateRange: { start: "", end: "" },
  selectedClient: null,
  showForm: false,
  formData: {
    Date_Facture: new Date().toISOString().split("T")[0],
    Statut_Vente: "Commandé",
    Client_ID: "",
    produitsVendus: [],
    remise_globale: 0,
    termes_conditions: "",
    remarques: "",
  },
  showDeleted: false,
  activeTab: "all",
};

const proformasReducer = (
  state: ProformasState,
  action: ProformasAction
): ProformasState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_PROFORMAS":
      return {
        ...state,
        proformas: action.payload.proformas,
        totalPages: Math.ceil(action.payload.total / state.itemsPerPage),
      };
    case "DELETE_PROFORMA":
      return {
        ...state,
        proformas: state.proformas.filter((p) => p.id !== action.payload),
      };
    case "SET_CLIENTS":
      return { ...state, clients: action.payload };
    case "SET_PRODUITS":
      return { ...state, produits: action.payload };
    case "SET_TAXES":
      return { ...state, taxes: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_SEARCH":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload, currentPage: 1 };
    case "SET_SELECTED_CLIENT":
      return { ...state, selectedClient: action.payload, currentPage: 1 };
    case "TOGGLE_FORM":
      return {
        ...state,
        showForm: action.payload,
      };
    case "SET_FORM_DATA":
      return { ...state, formData: action.payload };
    case "UPDATE_FORM_DATA":
      return { ...state, formData: action.payload };
    case "TOGGLE_SHOW_DELETED":
      return { ...state, showDeleted: action.payload, currentPage: 1 };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload, currentPage: 1 };
    case "SET_ITEMS_PER_PAGE":
      return { ...state, itemsPerPage: action.payload };
    case "RESET_FORM_DATA":
      return {
        ...state,
        formData: {
          Date_Facture: new Date().toISOString().split("T")[0],
          Statut_Vente: "Commandé",
          Client_ID: "",
          produitsVendus: [],
          remise_globale: 0,
          termes_conditions: "",
          remarques: "",
        },
      };
    case "UPDATE_PROFORMA_DETAIL":
      return {
        ...state,
        proformas: state.proformas.map((p) =>
          p.id === action.payload.id
            ? { ...p, produitsVendus: action.payload.produitsVendus }
            : p
        ),
      };
    default:
      return state;
  }
};

export const ProformasContext = createContext<{
  state: ProformasState;
  dispatch: React.Dispatch<ProformasAction>;
  api: typeof api;
} | null>(null);

interface ProformaFormProps {
  visible: boolean;
  onClose: () => void;
  refreshProformas: () => Promise<void>;
  onAfterSave?: () => void;
  warehouseName?: string | null;
}

export const ProformaForm: React.FC<ProformaFormProps> = ({
  visible,
  onClose,
  refreshProformas,
  onAfterSave,
  warehouseName,
}) => {
  const { state, dispatch, api } = useContext(ProformasContext)!;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [barcodeVisible, setBarcodeVisible] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const { selectedWarehouse, selectedCompany } = useSelection();
  const globalDiscountValue = Number(
    Form.useWatch("remise_globale", form) || 0,
  );
  const termsValue = Form.useWatch("termes_conditions", form);
  const remarksValue = Form.useWatch("remarques", form);
  const warehouseContextLabel =
    String(warehouseName || "").trim() ||
    (selectedWarehouse ? `Établissement #${selectedWarehouse}` : "Aucun établissement sélectionné");
  const additionalInformationSummary =
    termsValue || remarksValue
      ? [termsValue ? "Termes renseignés" : null, remarksValue ? "Remarques renseignées" : null]
          .filter(Boolean)
          .join(" · ")
      : "Termes, conditions et remarques";

  // useEffect simplifié pour initialiser le formulaire
  useEffect(() => {
    if (visible) {
      if (state.formData.id) {
        // Mode édition - Charger les données de la proforma
        form.setFieldsValue({
          Date_Facture: dayjs(state.formData.Date_Facture),
          Statut_Vente: state.formData.Statut_Vente,
          Client_ID: parseInt(state.formData.Client_ID), // Convertir en nombre pour correspondre aux valeurs du Select
          remise_globale: state.formData.remise_globale,
          termes_conditions: state.formData.termes_conditions,
          remarques: state.formData.remarques,
        });
      } else {
        // Mode création - Valeurs par défaut
        form.setFieldsValue({
          Date_Facture: dayjs(),
          Statut_Vente: "Commandé",
          Client_ID: undefined,
          remise_globale: 0,
          termes_conditions: "",
          remarques: "",
        });
      }
    }
  }, [visible, state.formData.id, state.formData.Date_Facture, state.formData.Statut_Vente,
    state.formData.Client_ID, state.formData.remise_globale, state.formData.termes_conditions,
    state.formData.remarques, form]);

  // Filtrer les clients en fonction du magasin sélectionné
  const filteredClients = state.clients.filter((client: any) => {
    // Si pas de magasin sélectionné, montrer tous les clients
    if (!selectedWarehouse) return true;

    // Si le client n'a pas de détail de magasin, ne pas le montrer
    if (!client.detail_warehouse_id) return false;

    // Vérifier si le client appartient au magasin sélectionné
    return client.detail_warehouse_id === selectedWarehouse;
  });

  const onFinish = async (values: any) => {
    // Vérification des données requises
    if (!selectedWarehouse) {
      message.error(
        "Veuillez sélectionner un établissement dans la barre de navigation"
      );
      return;
    }

    if (!selectedCompany) {
      message.error(
        "Veuillez sélectionner une entreprise dans la barre de navigation"
      );
      return;
    }

    if (!values.Client_ID) {
      message.error("Veuillez sélectionner un client");
      return;
    }

    if (state.formData.produitsVendus.length === 0) {
      message.error("Veuillez ajouter au moins un article");
      return;
    }

    try {
      const mappedProduitsVendus = state.formData.produitsVendus.map((p) => {
        const base = p.prix_unitaire_HT * p.quantite;
        const taxRate = p.taxe
          ? Number(state.taxes.find((t: any) => t.id === p.taxe)?.rate || 0)
          : 0;
        const montant_taxe = (base * taxRate) / 100;
        return {
          ...p,
          taxe: p.taxe ? Number(p.taxe) : null,
          montant_taxe: Number(montant_taxe) || 0,
          prix_unitaire_HT: Number(p.prix_unitaire_HT) || 0,
          quantite: Number(p.quantite) || 0,
          remise: Number(p.remise || 0),
        };
      });

      const totalTax = mappedProduitsVendus.reduce(
        (sum, p) => sum + (p.montant_taxe || 0),
        0
      );
      const montantTotalHT = mappedProduitsVendus.reduce(
        (sum, p) => sum + (p.prix_unitaire_HT || 0) * (p.quantite || 0),
        0
      );
      const totalRemisesCalc =
        mappedProduitsVendus.reduce((sum, p) => sum + (p.remise || 0), 0) +
        (values.remise_globale || 0);

      const montantTTC =
        Number(montantTotalHT) - Number(totalRemisesCalc) + Number(totalTax);

      const dataToSubmit = {
        ...values,
        Date_Facture: values.Date_Facture.format("YYYY-MM-DD"),
        produitsVendus: mappedProduitsVendus,
        Montant_Total_HT: montantTotalHT,
        Montant_TTC: montantTTC,
        Montant_Taxe: totalTax,
        warehouse_id: selectedWarehouse,
        company_id: selectedCompany,
      };

      setLoading(true);

      // Si le formulaire a un ID, c'est une mise à jour
      if (state.formData.id) {
        const result = await api.updateProforma(
          state.formData.id,
          dataToSubmit
        );
        if (result) {
          message.success("Proforma mise à jour avec succès");
        }
      } else {
        // Sinon, c'est une création
        const result = await api.createProforma(dataToSubmit);
        if (result) {
          message.success("Proforma créée avec succès");
        }
      }

      await refreshProformas();

      // Déclencher le rafraîchissement des détails si c'est une modification
      if (state.formData.id && onAfterSave) {
        onAfterSave();
      }

      onClose();
    } catch (error: any) {
      console.error("Erreur lors de la soumission du formulaire:", error);
      message.error(
        error.message || "Erreur lors de la soumission du formulaire"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    // Vérifier si le produit existe déjà dans la liste
    const existingIndex = state.formData.produitsVendus.findIndex(
      (p) => p.produit_id === product.id_produit
    );

    if (existingIndex >= 0) {
      // Si le produit existe déjà, augmenter la quantité de 1
      const updatedProducts = [...state.formData.produitsVendus];
      updatedProducts[existingIndex] = {
        ...updatedProducts[existingIndex],
        quantite: (updatedProducts[existingIndex].quantite || 0) + 1,
      };

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: { ...state.formData, produitsVendus: updatedProducts },
      });
    } else {
      // Sinon, ajouter le produit à la liste
      const newProduct: ProduitVendu = {
        produit_id: product.id_produit,
        item_type: product.item_type || "product",
        product_id: product.item_type === "service" ? null : product.id_produit,
        service_id: product.service_id ?? null,
        unit_id: product.unit_id ?? null,
        nom_produit: product.nom_produit,
        quantite: 1,
        prix_unitaire_HT: product.prix_vente,
        remise: 0,
        taxe: null, // Par défaut pas de taxe
        quantite_stock: product.quantite_stock,
      };

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          ...state.formData,
          produitsVendus: [...state.formData.produitsVendus, newProduct],
        },
      });
    }
  };

  const handleBarcodeScanned = async (barcode: string) => {
    try {
      // Rechercher le produit par code-barres
      const response = await api.fetchProduits({
        barcode: barcode,
        warehouse: selectedWarehouse,
      });

      if (response && response.products && response.products.length > 0) {
        const product = response.products[0];
        handleProductSelect({
          id_produit: product.id,
          nom_produit: product.name,
          prix_vente: product.sales_price,
          quantite_stock: product.current_stock,
          code_barre:
            product.barcode_value || product.barcode || product.item_code,
        });
        message.success(`Produit "${product.name}" ajouté`);
      } else {
        message.warning("Aucun produit trouvé avec ce code-barres");
      }
    } catch (error) {
      console.error("Erreur lors de la recherche par code-barres:", error);
      message.error("Erreur lors de la recherche du produit");
    }
  };

  // Calculer les montants totaux
  const subTotal = state.formData.produitsVendus.reduce(
    (sum, p) => sum + p.prix_unitaire_HT * p.quantite,
    0
  );

  const totalDiscount =
    state.formData.produitsVendus.reduce((sum, p) => sum + (p.remise || 0), 0) +
    globalDiscountValue;

  const totalTax = state.formData.produitsVendus.reduce((sum, p) => {
    if (!p.taxe) return sum;
    const taxRate = state.taxes.find((t: any) => t.id === p.taxe)?.rate || 0;
    return sum + (p.prix_unitaire_HT * p.quantite * taxRate) / 100;
  }, 0);

  const grandTotal = subTotal - totalDiscount + totalTax;

  return (
    <Modal
      className="sale-form-modal"
      key={state.formData.id ? `edit-${state.formData.id}` : "create"}
      title={
        <CommercialDocumentModalTitle
          kind="proforma"
          title={state.formData.id ? "Modifier la proforma" : "Nouvelle proforma"}
          subtitle={
            state.formData.id
              ? "Mettre à jour les articles et les conditions de la proforma"
              : "Sélectionner un client, préparer les articles et enregistrer la proforma"
          }
          contextLabel={warehouseContextLabel}
        />
      }
      open={visible}
      onCancel={onClose}
      width={window.innerWidth > 768 ? "96%" : "100%"}
      style={{ maxWidth: "1760px" }}
      bodyStyle={{
        maxHeight: "calc(100vh - 108px)",
        overflowY: "auto",
        padding: 0,
      }}
      footer={null}
    >
      <div className="sale-form-modal__content">
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            disabled={!selectedWarehouse}
            className="sale-form"
          >
            <div className="sale-form__layout">
              <main className="sale-form__work">
                {state.formData.id && (
                  <Alert
                    message={`Modification de ${
                      state.formData.invoice_number || `la proforma #${state.formData.id}`
                    }`}
                    description="Les informations existantes ont été chargées dans le formulaire."
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}
                {!selectedWarehouse && (
                  <Alert
                    message="Établissement requis"
                    description="Sélectionnez un établissement dans la barre de navigation avant de créer une proforma."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                )}

                <Row gutter={[12, 8]} className="sale-form__setup">
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="Client_ID"
                      label="Client"
                      rules={[{ required: true, message: "Sélectionnez un client" }]}
                    >
                      <Select
                        placeholder="Sélectionner un client"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option: any) =>
                          String(option?.children ?? "")
                            .toLowerCase()
                            .includes(input.trim().toLowerCase())
                        }
                        notFoundContent="Aucun client trouvé"
                      >
                        {filteredClients.map((client: any) => (
                          <Option key={client.id} value={client.id}>
                            {client.name ||
                              client.company_name ||
                              client.Nom_Raison_Sociale ||
                              "Client inconnu"}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="Date_Facture"
                      label="Date"
                      rules={[{ required: true, message: "Sélectionnez une date" }]}
                    >
                      <DatePicker
                        format="DD/MM/YYYY"
                        allowClear={false}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item
                      name="Statut_Vente"
                      label="Statut"
                      rules={[{ required: true, message: "Sélectionnez un statut" }]}
                    >
                      <Select>
                        <Option value="Commandé">Commandé</Option>
                        <Option value="En traitement">En traitement</Option>
                        <Option value="Expédié">Expédié</Option>
                        <Option value="Livré">Livré</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[12, 8]} className="sale-form__composer">
                  <Col xs={24} md={18}>
                    <Form.Item label="Ajouter un article">
                      <OrderProductSearch
                        key={`${selectedCompany}-${selectedWarehouse}-${state.formData.id || "create"}`}
                        className="sale-form-search"
                        orderType="proforma"
                        action={state.formData.id ? "edit" : "create"}
                        active={visible}
                        disabled={!selectedCompany || !selectedWarehouse}
                        onSelect={handleProductSelect}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label=" ">
                      <Button
                        className="sale-form__scanner"
                        block
                        icon={<QrcodeOutlined />}
                        onClick={() => setBarcodeVisible(true)}
                      >
                        Scanner un code-barres
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>

                <div className="sale-form-table-shell">
                  <Table
                    className="sale-form-table"
                    dataSource={state.formData.produitsVendus}
                    rowKey={(record, index) => `${record.produit_id}-${index}`}
                    pagination={false}
                    size="small"
                    scroll={{ x: 1050 }}
                    locale={{
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <span className="sale-form-table__empty">
                              <strong>Aucun article ajouté</strong>
                              <small>
                                Choisissez un produit ou un service, ou scannez un code-barres.
                              </small>
                            </span>
                          }
                        />
                      ),
                    }}
                  >
                    <Table.Column
                      title="Article"
                      dataIndex="nom_produit"
                      width={230}
                      fixed="left"
                      render={(value: string, record: ProduitVendu) => (
                        <div className="sale-form-item">
                          <span className="sale-form-item__thumbnail sale-form-item__thumbnail--fallback">
                            <PictureOutlined aria-hidden="true" />
                          </span>
                          <div className="sale-form-item__identity">
                            <strong title={value}>{value}</strong>
                            {record.item_type === "service" ? (
                              <span className="sale-form-item__meta">Service</span>
                            ) : record.quantite_stock !== undefined && (
                              <span className="sale-form-item__meta">
                                Stock disponible : {formatNumber(record.quantite_stock)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    />
                    <Table.Column
                      title="Prix unitaire HT"
                      dataIndex="prix_unitaire_HT"
                      width={155}
                      render={(value: any, record: any, index: number) => (
                        <InputNumber
                          min={0}
                          value={value}
                          addonAfter="CFA"
                          style={{ width: "100%" }}
                          formatter={(val: any) => (val ? formatNumber(val) : "")}
                          parser={(val: any) => val.replace(/\D/g, "")}
                          onChange={(newValue: any) => {
                            const updatedProducts = [...state.formData.produitsVendus];
                            updatedProducts[index].prix_unitaire_HT = newValue;
                            dispatch({
                              type: "UPDATE_FORM_DATA",
                              payload: { ...state.formData, produitsVendus: updatedProducts },
                            });
                          }}
                        />
                      )}
                    />
                    <Table.Column
                      title="Quantité"
                      dataIndex="quantite"
                      width={150}
                      render={(value: any, record: any, index: number) => {
                        const updateQuantity = (nextValue: number) => {
                          const updatedProducts = [...state.formData.produitsVendus];
                          updatedProducts[index].quantite = Math.max(1, nextValue || 1);
                          dispatch({
                            type: "UPDATE_FORM_DATA",
                            payload: { ...state.formData, produitsVendus: updatedProducts },
                          });
                        };
                        return (
                          <div className="sale-form-quantity-stepper">
                            <Button
                              aria-label={`Diminuer la quantité de ${record.nom_produit}`}
                              disabled={Number(value) <= 1}
                              onClick={() => updateQuantity(Number(value) - 1)}
                            >
                              −
                            </Button>
                            <InputNumber
                              min={1}
                              controls={false}
                              value={value}
                              aria-label={`Quantité de ${record.nom_produit}`}
                              onChange={(newValue: any) => updateQuantity(Number(newValue))}
                            />
                            <Button
                              aria-label={`Augmenter la quantité de ${record.nom_produit}`}
                              onClick={() => updateQuantity(Number(value) + 1)}
                            >
                              +
                            </Button>
                          </div>
                        );
                      }}
                    />
                    <Table.Column
                      title="Remise"
                      dataIndex="remise"
                      width={135}
                      render={(value: any, record: any, index: number) => (
                        <InputNumber
                          min={0}
                          value={value || 0}
                          addonAfter="CFA"
                          style={{ width: "100%" }}
                          formatter={(val: any) =>
                            val !== undefined && val !== null
                              ? formatNumber(Number(val))
                              : ""
                          }
                          parser={(val: any) => (val ? val.replace(/\D/g, "") : "")}
                          onChange={(newValue: any) => {
                            const updatedProducts = [...state.formData.produitsVendus];
                            updatedProducts[index].remise = newValue || 0;
                            dispatch({
                              type: "UPDATE_FORM_DATA",
                              payload: { ...state.formData, produitsVendus: updatedProducts },
                            });
                          }}
                        />
                      )}
                    />
                    <Table.Column
                      title="Taxe"
                      dataIndex="taxe"
                      width={145}
                      render={(value: any, record: any, index: number) => (
                        <Select
                          value={value}
                          style={{ width: "100%" }}
                          onChange={(newValue: any) => {
                            const updatedProducts = [...state.formData.produitsVendus];
                            updatedProducts[index].taxe = newValue;
                            dispatch({
                              type: "UPDATE_FORM_DATA",
                              payload: { ...state.formData, produitsVendus: updatedProducts },
                            });
                          }}
                        >
                          <Option value={null}>Aucune</Option>
                          {state.taxes.map((tax: any) => (
                            <Option key={tax.id} value={tax.id}>
                              {tax.name} ({tax.rate}%)
                            </Option>
                          ))}
                        </Select>
                      )}
                    />
                    <Table.Column
                      title="Total TTC"
                      width={125}
                      render={(text: any, record: any) => {
                        const base = record.prix_unitaire_HT * record.quantite;
                        const remise = record.remise || 0;
                        const taxRate = record.taxe
                          ? state.taxes.find((t: any) => t.id === record.taxe)?.rate || 0
                          : 0;
                        const tax = (base * taxRate) / 100;
                        return (
                          <strong className="sale-form-line-total">
                            {formatNumber(base - remise + tax)} CFA
                          </strong>
                        );
                      }}
                    />
                    <Table.Column
                      title="Actions"
                      width={78}
                      fixed="right"
                      render={(text: any, record: any, index: number) => (
                        <Button
                          className="sale-form-line-action"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={`Retirer ${record.nom_produit} de la proforma`}
                          onClick={() => {
                            const updatedProducts = [...state.formData.produitsVendus];
                            updatedProducts.splice(index, 1);
                            dispatch({
                              type: "UPDATE_FORM_DATA",
                              payload: { ...state.formData, produitsVendus: updatedProducts },
                            });
                          }}
                        />
                      )}
                    />
                  </Table>
                </div>

                <CommercialDocumentAdditionalInfo summary={additionalInformationSummary}>
                  <Row gutter={[12, 8]}>
                    <Col xs={24} md={12}>
                      <Form.Item name="termes_conditions" label="Termes et conditions">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="remarques" label="Remarques">
                        <Input.TextArea rows={3} />
                      </Form.Item>
                    </Col>
                  </Row>
                </CommercialDocumentAdditionalInfo>
              </main>

              <CommercialDocumentSummary
                ariaLabel="Résumé de la proforma"
                title="Résumé de la proforma"
                countLabel={
                  state.formData.produitsVendus.length === 0
                    ? "Aucun article"
                    : `${state.formData.produitsVendus.length} article${
                        state.formData.produitsVendus.length > 1 ? "s" : ""
                      }`
                }
                rows={[
                  { label: "Base HT", value: `${formatNumber(subTotal)} CFA` },
                  { label: "Remises", value: `${formatNumber(totalDiscount)} CFA` },
                  { label: "Taxes", value: `${formatNumber(totalTax)} CFA` },
                ]}
                beforeTotal={
                  <div className="sale-form-summary__control">
                    <Form.Item name="remise_globale" label="Remise globale">
                      <InputNumber
                        min={0}
                        addonAfter="CFA"
                        formatter={(value: any) =>
                          value !== undefined && value !== null
                            ? formatNumber(Number(value))
                            : ""
                        }
                        parser={(value: any) =>
                          value ? Number(value.replace(/\D/g, "")) : 0
                        }
                      />
                    </Form.Item>
                  </div>
                }
                totalLabel="Total TTC"
                totalValue={`${formatNumber(grandTotal)} CFA`}
                actions={
                  <>
                    <Button onClick={onClose}>Annuler</Button>
                    <Button type="primary" icon={<SaveOutlined />} htmlType="submit">
                      {state.formData.id ? "Mettre à jour la proforma" : "Enregistrer la proforma"}
                    </Button>
                  </>
                }
              >
                <div className="sale-form-summary__payment-readonly">
                  <span>Paiement</span>
                  <Tag color="blue">Non applicable</Tag>
                </div>
              </CommercialDocumentSummary>
            </div>
          </Form>
        </Spin>
      </div>

      {/* Scanner de code-barres */}
      {barcodeVisible && (
        <Modal
          title="Scanner un code-barres"
          open={barcodeVisible}
          onCancel={() => setBarcodeVisible(false)}
          footer={null}
        >
          <div style={{ textAlign: "center" }}>
            <BarcodeScanner onDetected={handleBarcodeScanned} />
          </div>
        </Modal>
      )}
    </Modal>
  );
};

// Composant BarcodeScanner
const BarcodeScanner: React.FC<{ onDetected: (barcode: string) => void }> = ({
  onDetected,
}) => {
  const [barcode, setBarcode] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBarcode(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && barcode.trim()) {
      onDetected(barcode.trim());
      setBarcode("");
    }
  };

  return (
    <div style={{ margin: "20px 0" }}>
      <p>Scannez un code-barres ou saisissez-le manuellement :</p>
      <Input
        placeholder="Code-barres..."
        value={barcode}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        autoFocus
      />
      <Button
        type="primary"
        onClick={() => {
          if (barcode.trim()) {
            onDetected(barcode.trim());
            setBarcode("");
          }
        }}
        style={{ marginTop: 10 }}
      >
        Valider
      </Button>
    </div>
  );
};

// Composant ProductSearch pour la recherche de produits
const ProductSearch: React.FC<{
  onSelect: (product: Product) => void;
  warehouseId?: number | null;
}> = ({ onSelect, warehouseId }) => {
  const [options, setOptions] = useState<any[]>([]);
  const [searchText, setSearchText] = useState("");
  const { state } = useContext(ProformasContext)!;

  const searchProducts = useCallback(
    debounce(async (value: string) => {
      if (!value || value.length < 2) {
        setOptions([]);
        return;
      }

      try {
        // Filtrer les produits localement (à adapter selon votre besoin)
        const filteredProducts = state.produits.filter(
          (p) =>
            p.nom_produit.toLowerCase().includes(value.toLowerCase()) &&
            (p.quantite_stock || 0) > 0
        );

        setOptions(
          filteredProducts.map((p) => ({
            value: p.id_produit,
            label: `${p.nom_produit} - ${formatNumber(p.prix_vente)} FCFA`,
            product: p,
          }))
        );
      } catch (error) {
        console.error("Erreur lors de la recherche des produits:", error);
      }
    }, 300),
    [state.produits]
  );

  useEffect(() => {
    searchProducts(searchText);
  }, [searchText, searchProducts]);

  return (
    <AutoComplete
      value={searchText}
      options={options}
      style={{ width: "100%" }}
      onSearch={(value) => setSearchText(value)}
      placeholder="Rechercher un produit..."
      onSelect={(value, option) => {
        const product = (option as any).product;
        onSelect(product);
        setSearchText("");
      }}
    />
  );
};

// Composant pour l'affichage détaillé des produits dans les lignes développées
const ExpandedRowContent: React.FC<{
  record: any;
  refreshTrigger?: number;
}> = ({ record, refreshTrigger }) => {
  const { state, api } = useContext(ProformasContext)!;
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadItems = async () => {
      try {
        setLoading(true);
        const orderItems = await api.fetchOrderItems(record.id);

        // Enrichir les items avec les noms des produits si nécessaire
        const enrichedItems = await Promise.all(
          orderItems.map(async (item: any) => {
            // Si l'item a déjà un product_name, l'utiliser
            if (item.product_name) {
              return item;
            }

            // Sinon, chercher dans l'état local
            const localProduct = state.produits.find(
              (p) => p.id_produit === item.product_id
            );
            if (localProduct) {
              return { ...item, product_name: localProduct.nom_produit };
            }

            // En dernier recours, essayer de récupérer le détail du produit
            try {
              const productDetails = await api.fetchProductDetails([
                item.product_id,
              ]);
              if (productDetails && productDetails.length > 0) {
                return {
                  ...item,
                  product_name:
                    productDetails[0].nom_produit || productDetails[0].name,
                };
              }
            } catch (error) {
              console.warn(
                `Impossible de récupérer le nom du produit ${item.product_id}:`,
                error
              );
            }

            // Si tout échoue, utiliser un nom par défaut
            return { ...item, product_name: `Produit #${item.product_id}` };
          })
        );

        setItems(enrichedItems);
      } catch (error) {
        console.error("Erreur lors du chargement des produits:", error);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [record.id, api, state.produits, refreshTrigger]);

  return (
    <Spin spinning={loading}>
      <Card title="Détails des produits" size="small" bordered={false}>
        <Table
          dataSource={items}
          rowKey="id"
          pagination={false}
          size="small"
          columns={[
            {
              title: "Produit",
              dataIndex: "product_name",
              render: (productName: string, record: any) => {
                return productName || `Produit #${record.product_id}`;
              },
            },
            {
              title: "Prix unitaire",
              dataIndex: "unit_price",
              render: (price: number) => `${formatNumber(price)} FCFA`,
            },
            {
              title: "Quantité",
              dataIndex: "quantity",
            },
            {
              title: "Remise",
              dataIndex: "discount_rate",
              render: (discount: number) =>
                discount ? `${formatNumber(discount)} FCFA` : "0 FCFA",
            },
            {
              title: "Taxe",
              dataIndex: "tax_id",
              render: (id: number) => {
                const tax = state.taxes.find((t: any) => t.id === id);
                return tax ? `${tax.name} (${tax.rate}%)` : "Aucune";
              },
            },
            {
              title: "Total",
              dataIndex: "subtotal",
              render: (subtotal: number) => `${formatNumber(subtotal)} FCFA`,
            },
          ]}
        />
      </Card>
    </Spin>
  );
};

// Composant pour la fenêtre modale de détail d'une proforma
interface ProformaDetailModalProps {
  visible: boolean;
  order: any;
  onClose: () => void;
  produits: Product[];
  taxes: any[];
  clients: any[];
  refreshOrderDetails: (record: any) => Promise<void>;
  refreshProformas: () => Promise<void>;
  onDownloadInvoice?: (record: any) => Promise<void>;
}

export const ProformaDetailModal: React.FC<ProformaDetailModalProps> = ({
  visible,
  order,
  onClose,
  produits,
  taxes,
  clients,
  refreshOrderDetails,
  refreshProformas,
  onDownloadInvoice,
}) => {
  const { api } = useContext(ProformasContext)!;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const auth: any = useAuth(); // Utiliser le type any pour contourner les erreurs TypeScript

  // Fonction de vérification des permissions sécurisée
  const hasPermission = (permission: string): boolean => {
    // Ajouter des logs pour déboguer

    const result =
      typeof auth?.hasPermission === "function"
        ? auth.hasPermission(permission)
        : false;

    return result;
  };

  if (!order) return null;

  const client = clients.find((c) => c.id === order.user_id) || {
    name: "Client inconnu",
  };

  // Calculer les totaux à partir des items si disponibles
  const items = order.items || [];
  const subtotal =
    order.subtotal ||
    items.reduce((sum: number, item: any) => sum + item.subtotal, 0);
  const totalDiscount = order.discount || 0;
  const totalTax = order.tax_amount || 0;
  const total = order.total || subtotal - totalDiscount + totalTax;

  // Fonction pour ouvrir les détails de la vente générée à partir de la proforma
  const openSaleDetails = () => {
    if (order.converted_sale_id) {
      navigate(`/gestion-ventes/ventes?id=${order.converted_sale_id}`);
    }
  };

  const handleConvert = async () => {
    // Vérifier si l'utilisateur a la permission de convertir
    if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.convert")) {
      message.error(
        "Vous n'avez pas les droits nécessaires pour convertir une proforma en vente."
      );
      return;
    }

    try {
      Modal.confirm({
        title: "Confirmation",
        content:
          "Êtes-vous sûr de vouloir convertir cette proforma en commande de vente?",
        okText: "Oui",
        cancelText: "Non",
        onOk: async () => {
          setLoading(true);
          try {
            const result = await api.convertToSale(order.id, order.company_id);

            if (result) {
              message.success(
                "Proforma convertie en commande de vente avec succès"
              );

              // Pour s'assurer que les données locales sont correctement mises à jour

              // Mettre à jour les propriétés de l'objet order directement
              order.is_converted = 1;
              order.converted_sale_id = result.orderId || result.id;
              order.sale_invoice_number = result.invoice_number;
              void invalidateBusinessResources(queryClient, {
                userId: auth?.user?.id,
                companyId: order.company_id,
                warehouseId: order.warehouse_id,
              }, "sale");

              // Force le rendu du composant en modifiant l'état
              setActiveTab(activeTab);

              // Rafraîchir la liste des proformas
              await refreshProformas();

              // Rafraîchir les détails de la proforma pour obtenir le lien vers la vente
              if (refreshOrderDetails) {
                try {
                  await refreshOrderDetails(order);

                  // Afficher les détails après rafraîchissement pour vérifier
                } catch (refreshError) {
                  console.error(
                    "Erreur lors du rafraîchissement des détails:",
                    refreshError
                  );
                }
              }

              // Proposer de naviguer vers la vente créée
              Modal.confirm({
                title: "Vente créée",
                content: "Souhaitez-vous accéder à la vente créée?",
                okText: "Oui",
                cancelText: "Non",
                onOk: () => {
                  // Naviguer vers le module de gestion des ventes avec l'ID
                  navigate(`/gestion-ventes/ventes?id=${result.orderId || result.id}`);
                },
              });
            }
          } catch (error) {
            console.error("Erreur lors de la conversion:", error);
            message.error(error instanceof Error ? error.message : "Erreur lors de la conversion en vente");
          } finally {
            setLoading(false);
          }
        },
      });
    } catch (error) {
      console.error("Erreur lors de la conversion:", error);
      message.error(error instanceof Error ? error.message : "Erreur lors de la conversion en vente");
      setLoading(false);
    }
  };

  // Vérifier si la proforma a déjà été convertie en vente
  const isConverted = order.is_converted === 1 || order.is_converted === true;

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>
            Détails Proforma #{order.invoice_number || order.reference}{" "}
            {order.is_deleted === 1 && <Tag color="red">Supprimé</Tag>}
          </span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button
          key="download"
          type="default"
          icon={<DownloadOutlined />}
          onClick={() => {
            if (onDownloadInvoice) {
              onDownloadInvoice(order);
            }
          }}
        >
          Télécharger
        </Button>,
        isConverted ? (
          <Button
            key="viewSale"
            type="primary"
            icon={<EyeOutlined />}
            onClick={openSaleDetails}
          >
            Voir la facture de vente
          </Button>
        ) : (
          // Utiliser un composant Fragment pour rendre la condition plus claire
          <>
            {/* Vérification de permission pour le bouton de conversion */}
            {(() => {
              const canConvert = hasPermission(
                "Gestion Commerciale.Ventes.ProformaDevis.convert"
              );

              return canConvert ? (
                <Button
                  key="convert"
                  type="primary"
                  icon={<SwapOutlined />}
                  onClick={handleConvert}
                  loading={loading}
                  disabled={
                    order.order_status === "Annulé" ||
                    order.is_deleted ||
                    isConverted
                  }
                >
                  Convertir en vente
                </Button>
              ) : null;
            })()}
          </>
        ),
        hasPermission("Gestion Commerciale.Ventes.ProformaDevis.edit") &&
          !order?.is_deleted &&
          !order?.is_converted && (
            <Button
              key="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                onClose();
                // Code pour ouvrir l'édition
              }}
            >
              Modifier
            </Button>
          ),
        hasPermission("Gestion Commerciale.Ventes.ProformaDevis.delete") &&
          !order?.is_deleted &&
          !order?.is_converted && (
            <Button
              key="delete"
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                // Code pour confirmer la suppression
                Modal.confirm({
                  title: "Supprimer cette proforma ?",
                  content: "Cette action est irréversible.",
                  onOk: async () => {
                    try {
                      await api.deleteProforma(order.id);
                      message.success("Proforma supprimée avec succès");
                      refreshProformas();
                      onClose();
                    } catch (error) {
                      console.error("Erreur lors de la suppression:", error);
                      message.error("Erreur lors de la suppression");
                    }
                  },
                });
              }}
            >
              Supprimer
            </Button>
          ),
      ].filter(Boolean)} // Filtrer les valeurs false/null pour éviter les erreurs React
    >
      <Spin spinning={loading}>
        {isConverted && (
          <Alert
            message={
              <Space>
                <span>Cette proforma a été convertie en facture de vente.</span>
                <button
                  onClick={openSaleDetails}
                  style={{
                    cursor: "pointer",
                    color: "#1890ff",
                    background: "none",
                    border: "none",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                  type="button"
                >
                  Voir la facture #
                  {order.sale_invoice_number ||
                    `Vente #${order.converted_sale_id}`}
                </button>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "details",
              label: "Informations générales",
              children: (
                <>
                  <Card title="Informations de la proforma">
                    <Descriptions bordered column={2} size="small">
                      <Descriptions.Item label="N° Proforma">
                        {order.invoice_number}
                      </Descriptions.Item>
                      <Descriptions.Item label="Date">
                        {dayjs(order.order_date).format("DD/MM/YYYY")}
                      </Descriptions.Item>
                      <Descriptions.Item label="Client">
                        {client.name || client.company_name || "Client inconnu"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Établissement">
                        {order.warehouse_name || (order.warehouse_id ? `Établissement #${order.warehouse_id}` : "Non spécifié")}
                      </Descriptions.Item>
                      <Descriptions.Item label="Statut">
                        <Tag
                          color={
                            order.order_status === "Annulé"
                              ? "red"
                              : order.order_status === "En attente"
                              ? "orange"
                              : "green"
                          }
                        >
                          {order.order_status || "Non spécifié"}
                        </Tag>
                      </Descriptions.Item>
                      {isConverted && (
                        <Descriptions.Item label="Vente générée">
                          <button
                            onClick={openSaleDetails}
                            style={{
                              cursor: "pointer",
                              color: "#1890ff",
                              background: "none",
                              border: "none",
                              padding: 0,
                              textDecoration: "underline",
                            }}
                            type="button"
                          >
                            {order.sale_invoice_number || "Voir la vente"}
                          </button>
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="Montant Total HT">
                        {formatNumber(subtotal)} FCFA
                      </Descriptions.Item>
                      <Descriptions.Item label="Remise">
                        {formatNumber(totalDiscount)} FCFA
                      </Descriptions.Item>
                      <Descriptions.Item label="Taxes">
                        {formatNumber(totalTax)} FCFA
                      </Descriptions.Item>
                      <Descriptions.Item label="Montant Total TTC">
                        {formatNumber(total)} FCFA
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>

                  <div style={{ marginTop: "20px" }}>
                    <h3>Produits</h3>
                    <Table
                      dataSource={items}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    >
                      <Table.Column
                        title="Produit"
                        render={(_, record: any) => {
                          const productId = record.product_id;
                          const productName =
                            record.product_name ||
                            produits.find((p) => p.id_produit === productId)
                              ?.nom_produit ||
                            `Produit #${productId}`;
                          return productName;
                        }}
                      />
                      <Table.Column
                        title="Prix unitaire"
                        dataIndex="unit_price"
                        render={(price) => `${formatNumber(price)} FCFA`}
                      />
                      <Table.Column title="Quantité" dataIndex="quantity" />
                      <Table.Column
                        title="Remise"
                        dataIndex="discount_rate"
                        render={(discount) =>
                          discount ? `${formatNumber(discount)} FCFA` : "0 FCFA"
                        }
                      />
                      <Table.Column
                        title="Taxe"
                        dataIndex="tax_id"
                        render={(id) => {
                          if (!id) return "Aucune";
                          const tax = taxes.find((t) => t.id === id);
                          return tax ? `${tax.name} (${tax.rate}%)` : "Aucune";
                        }}
                      />
                      <Table.Column
                        title="Total"
                        dataIndex="subtotal"
                        render={(subtotal) => `${formatNumber(subtotal)} FCFA`}
                      />
                    </Table>
                  </div>

                  <Card style={{ marginTop: "20px" }} title="Notes et termes">
                    <Descriptions bordered column={1} size="small">
                      <Descriptions.Item label="Remarques">
                        {order.notes || "Aucune remarque"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Termes et conditions">
                        {order.terms_condition || "Aucun terme spécifié"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </>
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};

interface ProformasTableProps {
  refreshProformas: () => Promise<void>;
}

const ProformasTable: React.FC<ProformasTableProps> = ({
  refreshProformas,
}) => {
  const { state, dispatch, api } = useContext(ProformasContext)!;
  const [detailProforma, setDetailProforma] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const { selectedWarehouse } = useSelection();
  const auth: any = useAuth(); // Utiliser le type any pour contourner les erreurs TypeScript

  // Fonction de vérification des permissions sécurisée
  const hasPermission = (permission: string): boolean => {
    return typeof auth?.hasPermission === "function"
      ? auth.hasPermission(permission)
      : false;
  };

  useEffect(() => {
    let cancelled = false;

    const fetchWarehouseInfo = async () => {
      setWarehouseData(null);
      if (!selectedWarehouse) return;

      try {
        const warehouse = await api.fetchWarehouse(selectedWarehouse);
        if (!cancelled) {
          setWarehouseData(warehouse);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors de la récupération de l'établissement:", error);
        }
      }
    };

    void fetchWarehouseInfo();
    return () => {
      cancelled = true;
    };
  }, [selectedWarehouse, api]);

  const loadProformaDetails = async (record: any) => {
    try {
      setLoading(true);

      // Récupérer les détails de la proforma et ses éléments
      const proforma = await api.fetchProformaDetail(record.id);
      const items = await api.fetchOrderItems(record.id);

      // Si la proforma a été convertie en vente, récupérer les informations de la vente
      if (
        (proforma.is_converted === 1 || proforma.is_converted === true) &&
        proforma.converted_sale_id
      ) {
        try {
          const saleResponse = await fetch(
            `${BASE_URL}/api/orders/${proforma.converted_sale_id}`,
            {
              headers: getAuthHeaders(),
            }
          );
          if (saleResponse.ok) {
            const saleData = await saleResponse.json();
            proforma.sale_invoice_number = saleData.invoice_number;
          }
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des détails de la vente:",
            error
          );
        }
      }

      return {
        ...proforma,
        warehouse_name: proforma.warehouse_name ||
          (Number(record.warehouse_id) === Number(proforma.warehouse_id) ? record.warehouse_name : undefined),
        items,
      };
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error);
      message.error("Erreur lors du chargement des détails");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadProformaDetailsForModal = async (record: any) => {
    try {
      setLoading(true);
      const details = await loadProformaDetails(record);

      if (details) {
        // Mettre à jour le state local
        setDetailProforma({
          ...details,
          // S'assurer que ces propriétés sont correctement définies
          is_converted: details.is_converted || 0,
        });

        setShowDetailModal(true);
      }
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails pour le modal:",
        error
      );
      message.error("Erreur lors du chargement des détails");
    } finally {
      setLoading(false);
    }
  };

  const loadProformaForEdit = async (record: any) => {
    // Vérifier si l'utilisateur a la permission d'éditer
    if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.edit")) {
      message.error(
        "Vous n'avez pas les droits nécessaires pour modifier une proforma/devis."
      );
      return;
    }

    try {
      setLoading(true);
      const proforma = await api.fetchProformaDetail(record.id);
      const items = await api.fetchOrderItems(record.id);

      if (!proforma) {
        message.error("Impossible de charger les détails de la proforma");
        return;
      }

      // Convertir les données pour le format du formulaire
      const proformaData = {
        id: proforma.id,
        invoice_number: proforma.invoice_number,
        Date_Facture: proforma.order_date,
        Statut_Vente: proforma.order_status,
        Client_ID: String(proforma.user_id), // Convertir en string pour compatibilité avec le formulaire
        remise_globale: proforma.discount || 0,
        termes_conditions: proforma.terms_condition || "",
        remarques: proforma.notes || "",
        produitsVendus: saleFormData(proforma, items || []).produitsVendus,
      };

      // Mettre à jour les données du formulaire
      dispatch({ type: "SET_FORM_DATA", payload: proformaData });

      // Ouvrir le formulaire immédiatement après avoir mis à jour les données
      dispatch({ type: "TOGGLE_FORM", payload: true });
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error);
      message.error("Erreur lors du chargement des détails");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour forcer le rechargement des détails des produits après modification
  const triggerDetailRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDelete = async (id: number) => {
    // Vérifier si l'utilisateur a la permission de supprimer
    if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.delete")) {
      message.error(
        "Vous n'avez pas les droits nécessaires pour supprimer une proforma/devis."
      );
      return;
    }

    Modal.confirm({
      title: "Confirmation",
      content: "Êtes-vous sûr de vouloir supprimer cette proforma?",
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          await api.deleteProforma(id);
          message.success("Proforma supprimée avec succès");
          dispatch({ type: "DELETE_PROFORMA", payload: id });
          await refreshProformas();
        } catch (error) {
          console.error("Erreur lors de la suppression:", error);
          message.error("Erreur lors de la suppression");
        }
      },
    });
  };

  const handleRestore = async (id: number) => {
    Modal.confirm({
      title: "Confirmation",
      content: "Êtes-vous sûr de vouloir restaurer cette proforma?",
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          await api.restoreProforma(id);
          message.success("Proforma restaurée avec succès");
          await refreshProformas();
        } catch (error) {
          console.error("Erreur lors de la restauration:", error);
          message.error("Erreur lors de la restauration");
        }
      },
    });
  };

  const handleCancel = async (id: number) => {
    Modal.confirm({
      title: "Confirmation",
      content: "Êtes-vous sûr de vouloir annuler cette proforma?",
      okText: "Oui",
      cancelText: "Non",
      onOk: async () => {
        try {
          await api.cancelProforma(id);
          message.success("Proforma annulée avec succès");
          await refreshProformas();
        } catch (error) {
          console.error("Erreur lors de l'annulation:", error);
          message.error("Erreur lors de l'annulation");
        }
      },
    });
  };

  const handleDownloadInvoice = async (record: any) => {
    try {
      setLoading(true);
      message.loading({
        content: "Préparation de la facture...",
        key: "invoice-loading",
      });

      // Récupérer les détails complets de la proforma
      const proformaDetail = await api.fetchProformaDetail(
        record.id.toString()
      );
      let produitsVendus = proformaDetail.produitsVendus || [];
      if (produitsVendus.length === 0) {
        const orderItems = await api.fetchOrderItems(record.id.toString());
        produitsVendus = orderItems;
        proformaDetail.produitsVendus = produitsVendus;
      }

      // Enrichir les détails des produits
      try {
        // S'assurer que nous avons bien tous les IDs produits quelle que soit la nomenclature utilisée
        const productIds = produitsVendus
          .map((p: any) => {
            // Considérer toutes les variations possibles de l'ID du produit
            const productId = p.produit_id || p.product_id;
            if (!productId) {
              console.warn("Produit sans ID détecté:", p);
            }
            return productId;
          })
          .filter(Boolean);

        if (productIds.length > 0) {
          const productDetails = await api.fetchProductDetails(productIds);

          produitsVendus = produitsVendus.map((p: any) => {
            const productId = p.produit_id || p.product_id;
            const details = productDetails.find((d: any) => d.id === productId);

            if (details) {
              return {
                ...p,
                unit_short_name: details.unit_short_name || "",
                unit_name: details.unit_name || "",
                product_name:
                  details.name ||
                  p.nom_produit ||
                  p.product_name ||
                  `Produit #${productId}`,
              };
            }

            // Si pas de détails trouvés, garder les informations existantes
            return {
              ...p,
              product_name:
                p.nom_produit || p.product_name || `Produit #${productId}`,
            };
          });

          proformaDetail.produitsVendus = produitsVendus;
        }
      } catch (enrichError) {
        console.error(
          "Erreur lors de l'enrichissement des produits:",
          enrichError
        );
        // Ne pas bloquer le processus en cas d'erreur, continuer avec les données disponibles
      }

      // Récupérer les informations du magasin
      const warehouse = await api.fetchWarehouse(record.warehouse_id);

      // Préparer l'objet invoiceData
      const invoiceData = {
        ...proformaDetail,
        produitsVendus,
        warehouse,
        client: state.clients.find((c: any) => c.id === proformaDetail.user_id),
      };

      // Générer le contenu HTML de la facture proforma
      const invoiceHTML = generateProformaHTML({
        order: invoiceData,
        clientObj: invoiceData.client,
        warehouse: warehouse,
        totals: {
          subtotal: invoiceData.subtotal,
          totalDiscount: invoiceData.discount || 0,
          totalTax: invoiceData.tax_amount || 0,
          total: invoiceData.total,
        },
        formattedDate: dayjs(invoiceData.order_date).format("DD/MM/YYYY"),
        formatNumber: formatNumber,
      });

      // Nom du fichier
      const fileName = `Proforma_${invoiceData.invoice_number}.pdf`;

      // NOUVEAU: Utiliser l'endpoint léger pour la génération PDF
      // CHOIX: Lightweight (html-pdf-node/html-pdf) ou Puppeteer (fallback)
      const useLightweightPdf = true; // ✅ Utiliser la nouvelle approche légère par défaut

      try {
        let response;

        if (useLightweightPdf) {
          // 🎯 NOUVELLE API: Lightweight PDF (html-pdf-node + fallbacks)

          // Préparer les données pour le nouvel endpoint
          const orderData = {
            ...invoiceData,
            htmlContent: invoiceHTML, // Inclure le HTML généré
          };

          response = await fetch(
            `${BASE_URL}/api/orders/generate-proforma-pdf-react`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                orderData: orderData,
                fileName: fileName,
              }),
            }
          );
        } else {
          // 🐌 ANCIENNE API: Puppeteer (compatible mais lourde)
          response = await fetch(
            `${BASE_URL}/api/orders/generate-proforma-pdf-react`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                orderData: invoiceData,
                fileName: fileName,
              }),
            }
          );
        }

        if (!response.ok) {
          // Si la réponse n'est pas OK, tenter d'extraire le message d'erreur
          let errorMessage = "Erreur serveur lors de la génération du PDF";
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (e) {
            console.error("Impossible de parser l'erreur JSON:", e);
          }
          throw new Error(errorMessage);
        }

        // Récupérer le blob PDF
        const pdfBlob = await response.blob();

        // Créer une URL à partir du blob
        const pdfUrl = URL.createObjectURL(pdfBlob);

        // Créer un lien invisible et déclencher le téléchargement
        const downloadLink = document.createElement("a");
        downloadLink.href = pdfUrl;
        downloadLink.download = fileName;
        downloadLink.style.display = "none";
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Nettoyer
        setTimeout(() => {
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(pdfUrl);
        }, 100);

        message.success({
          content: useLightweightPdf
            ? "Facture générée avec succès"
            : "Facture générée avec succès",
          key: "invoice-loading",
        });
      } catch (pdfError: unknown) {
        console.error("Erreur lors de la génération du PDF:", pdfError);

        // Si l'endpoint léger a échoué, essayer l'ancien endpoint comme fallback
        if (useLightweightPdf) {
          console.warn(
            "Endpoint léger a échoué, tentative avec l'ancien endpoint..."
          );
          try {
            const fallbackResponse = await fetch(
              `${BASE_URL}/api/orders/generate-proforma-pdf-react`,
              {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({
                  orderData: invoiceData,
                  fileName: fileName,
                }),
              }
            );

            if (!fallbackResponse.ok) {
              let errorMessage = "Erreur serveur lors de la génération du PDF";
              try {
                const errorData = await fallbackResponse.json();
                errorMessage = errorData.error || errorMessage;
              } catch (e) {
                console.error("Impossible de parser l'erreur JSON:", e);
              }
              throw new Error(errorMessage);
            }

            const pdfBlob = await fallbackResponse.blob();
            const pdfUrl = URL.createObjectURL(pdfBlob);

            const downloadLink = document.createElement("a");
            downloadLink.href = pdfUrl;
            downloadLink.download = fileName;
            downloadLink.style.display = "none";
            document.body.appendChild(downloadLink);
            downloadLink.click();

            setTimeout(() => {
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(pdfUrl);
            }, 100);

            message.success({
              content: "Facture générée avec succès (version standard)",
              key: "invoice-loading",
            });
          } catch (fallbackError: unknown) {
            console.error("Fallback également échoué:", fallbackError);
            message.error(
              "Erreur lors de la génération de la facture: " +
                (fallbackError instanceof Error
                  ? fallbackError.message
                  : "Erreur inconnue")
            );
          }
        } else {
          message.error(
            "Erreur lors de la génération de la facture: " +
              (pdfError instanceof Error ? pdfError.message : "Erreur inconnue")
          );
        }
      }
    } catch (error) {
      console.error(
        "Erreur lors de la génération de la facture proforma:",
        error
      );
      message.error("Erreur lors de la génération de la facture proforma");
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    try {
      setLoading(true);
      // Exporter les données vers Excel
      const dataForExcel = state.proformas.map((p: any) => ({
        Numéro: p.invoice_number,
        Date: dayjs(p.order_date).format("DD/MM/YYYY"),
        Client:
          state.clients.find((c: any) => c.id === p.user_id)?.name ||
          "Client inconnu",
        Établissement:
          p.warehouse_name || `Établissement #${p.warehouse_id}` || "Non spécifié",
        Statut: p.order_status,
        "Total HT": p.subtotal,
        "Total TTC": p.total,
      }));

      const ws = XLSX.utils.json_to_sheet(dataForExcel);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Proformas");

      // Générer le fichier Excel
      XLSX.writeFile(wb, "Proformas.xlsx");

      message.success("Export Excel réussi");
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      message.error("Erreur lors de l'export Excel");
    } finally {
      setLoading(false);
    }
  };

  const columns: TableProps<any>["columns"] = [
    {
      title: "Numéro",
      dataIndex: "invoice_number",
      key: "invoice_number",
      sorter: (a: any, b: any) =>
        a.invoice_number.localeCompare(b.invoice_number),
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date: string) => dayjs(date).format("DD/MM/YYYY"),
      sorter: (a: any, b: any) =>
        new Date(a.order_date).getTime() - new Date(b.order_date).getTime(),
      responsive: ["xs"],
    },
    {
      title: "Client",
      dataIndex: "user_id",
      key: "user_id",
      render: (clientId: number) => {
        const client = state.clients.find((c: any) => c.id === clientId);
        return client ? client.name : "Client inconnu";
      },
      responsive: ["md"],
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      render: (warehouseName: string, record: any) => {
        const displayName =
          warehouseName || `Établissement #${record.warehouse_id}` || "Non spécifié";
        const isCurrentWarehouse = record.warehouse_id === selectedWarehouse;

        return (
          <span
            style={{
              fontWeight: isCurrentWarehouse ? "bold" : "normal",
              color: isCurrentWarehouse ? "#1890ff" : "inherit",
            }}
          >
            {displayName}
            {isCurrentWarehouse && " ✓"}
          </span>
        );
      },
      responsive: ["lg"],
    },
    {
      title: "Statut",
      key: "conversion_status",
      align: "center" as const,
      render: function (value: any, record: any) {
        return (
          <Space size="middle">
            {record.order_status === "Annulé" ? (
              <Tag color="red">Annulée</Tag>
            ) : record.is_converted ? (
              <Tag color="blue">Convertie</Tag>
            ) : (
              <Tag color="green">Active</Tag>
            )}
          </Space>
        );
      },
      responsive: ["lg"],
    },
    {
      title: "Montant HT",
      dataIndex: "subtotal",
      key: "subtotal",
      render: (amount: number) => `${formatNumber(amount || 0)} FCFA`,
      sorter: (a: any, b: any) => (a.subtotal || 0) - (b.subtotal || 0),
      responsive: ["md"],
    },
    {
      title: "Montant Taxes",
      dataIndex: "tax_amount",
      key: "tax_amount",
      render: (amount: number) => `${formatNumber(amount || 0)} FCFA`,
      sorter: (a: any, b: any) => (a.tax_amount || 0) - (b.tax_amount || 0),
      responsive: ["lg"],
    },
    {
      title: "Montant TTC",
      dataIndex: "total",
      key: "total",
      render: (amount: number) => `${formatNumber(amount)} FCFA`,
      sorter: (a: any, b: any) => a.total - b.total,
      responsive: ["xs"],
    },
    {
      title: "Actions",
      key: "actions",
      width: 72,
      align: "center" as const,
      render: (_: unknown, record: any) => {
        const canEdit = hasPermission(
          "Gestion Commerciale.Ventes.ProformaDevis.edit"
        );
        const canDelete = hasPermission(
          "Gestion Commerciale.Ventes.ProformaDevis.delete"
        );
        const editDisabled = Boolean(record.is_deleted || record.is_converted);
        const actionItems: any[] = [
          {
            key: "view",
            icon: <EyeOutlined />,
            label: "Voir le détail",
            onClick: () => loadProformaDetailsForModal(record),
          },
        ];

        if (canEdit) {
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: editDisabled ? "Modification indisponible" : "Modifier",
            disabled: editDisabled,
            onClick: () => loadProformaForEdit(record),
          });
        }

        if (!record.is_deleted && record.order_status !== "Annulé") {
          actionItems.push({
            key: "convert",
            icon: <SwapOutlined />,
            label: record.is_converted ? "Proforma déjà convertie" : "Convertir",
            disabled: Boolean(record.is_converted),
            onClick: () => loadProformaDetailsForModal(record),
          });
        }

        if (record.is_deleted || canDelete) {
          actionItems.push({ type: "divider" });
        }

        if (record.is_deleted) {
          actionItems.push({
            key: "restore",
            icon: <UndoOutlined />,
            label: "Restaurer",
            onClick: () => handleRestore(record.id),
          });
        } else if (canDelete) {
          actionItems.push({
            key: "delete",
            icon: <DeleteOutlined />,
            label: record.is_converted
              ? "Suppression indisponible"
              : "Supprimer",
            disabled: Boolean(record.is_converted),
            danger: !record.is_converted,
            onClick: () => handleDelete(record.id),
          });
        }

        return (
          <RowActionsMenu
            items={actionItems}
            ariaLabel={`Afficher les actions de la proforma ${
              record.invoice_number || record.id
            }`}
          />
        );
      },
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16, textAlign: "right" }}>
        <Button
          icon={<FileExcelOutlined style={{ color: "#217346" }} />}
          onClick={handleExportToExcel}
        >
          Exporter sur Excel
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table
          dataSource={state.proformas}
          columns={columns}
          rowKey="id"
          scroll={{ x: "max-content" }}
          expandable={{
            expandedRowRender: (record) => (
              <ExpandedRowContent
                record={record}
                refreshTrigger={refreshTrigger}
              />
            ),
            expandRowByClick: false,
          }}
          pagination={{
            current: state.currentPage,
            pageSize: state.itemsPerPage,
            total: state.totalPages * state.itemsPerPage,
            onChange: (page) => {
              dispatch({ type: "SET_PAGE", payload: page });
            },
            onShowSizeChange: (current, size) => {
              dispatch({ type: "SET_ITEMS_PER_PAGE", payload: size });
              dispatch({ type: "SET_PAGE", payload: 1 }); // Revenir à la première page après changement de taille
            },
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} proformas`,
            pageSizeOptions: ["10", "20", "50", "100"],
            position: ["bottomRight"],
          }}
          sortDirections={["descend", "ascend"]}
          loading={state.loading}
        />
      </div>

      {/* Modal pour afficher les détails */}
      <ProformaDetailModal
        order={detailProforma}
        visible={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        produits={state.produits}
        taxes={state.taxes}
        clients={state.clients}
        refreshOrderDetails={loadProformaDetailsForModal}
        refreshProformas={refreshProformas}
        onDownloadInvoice={handleDownloadInvoice}
      />

      {state.showForm && (
        <ProformaForm
          visible={state.showForm}
          onClose={() => {
            // Réinitialiser complètement les données du formulaire à la fermeture
            dispatch({ type: "RESET_FORM_DATA" });
            dispatch({ type: "TOGGLE_FORM", payload: false });
          }}
          refreshProformas={refreshProformas}
          onAfterSave={triggerDetailRefresh}
          warehouseName={
            warehouseData?.name ||
            warehouseData?.warehouse_name ||
            warehouseData?.data?.name ||
            warehouseData?.data?.warehouse_name ||
            null
          }
        />
      )}
    </>
  );
};

// Composant principal
const GestionProformas: React.FC = () => {
  const [state, dispatch] = useReducer(proformasReducer, initialState);
  const proformasRequestRef = useRef(0);
  const [currentWarehouse, setCurrentWarehouse] = useState<number | null>(null);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const { selectedWarehouse, selectedCompany } = useSelection();
  const { taxes } = useTaxes(api); // Assuming 'api' is correctly defined above
  const auth: any = useAuth(); // Utiliser le type any pour contourner les erreurs TypeScript

  // Fonction de vérification des permissions sécurisée
  const hasPermission = (permission: string): boolean => {
    // console.log(`[GestionProformas] Vérification permission: ${permission}`);
    const result =
      typeof auth?.hasPermission === "function"
        ? auth.hasPermission(permission)
        : false;
    // console.log(`[GestionProformas] Résultat vérification: ${result}`);
    return result;
  };

  // Vérifier si l'utilisateur a la permission de voir ce module
  useEffect(() => {
    if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.view")) {
      message.error(
        "Vous n'avez pas les droits nécessaires pour accéder à ce module."
      );
    }
  }, [auth]); // Added auth to dependency array

  useEffect(() => {
    dispatch({ type: "SET_TAXES", payload: taxes });
  }, [taxes]);

  // Surveiller les changements de magasin sélectionné
  useEffect(() => {
    if (selectedWarehouse !== currentWarehouse) {
      setCurrentWarehouse(selectedWarehouse);
    }
  }, [selectedWarehouse, currentWarehouse, selectedCompany]);

  // Charger les données du magasin sélectionné
  useEffect(() => {
    const fetchWarehouseInfo = async () => {
      if (selectedWarehouse) {
        try {
          const warehouse = await api.fetchWarehouse(selectedWarehouse);
          setWarehouseData(warehouse);
        } catch (error) {
          console.error("Erreur lors de la récupération de l'établissement:", error);
          setWarehouseData(null);
        }
      } else {
        setWarehouseData(null);
      }
    };
    fetchWarehouseInfo();
  }, [selectedWarehouse]);

  const refreshProformas = useCallback(async () => {
    const requestId = ++proformasRequestRef.current;
    // Ne pas exécuter si aucun magasin n'est sélectionné
    if (!selectedWarehouse) {
      dispatch({ type: "SET_PROFORMAS", payload: { proformas: [], total: 0 } });
      dispatch({ type: "SET_LOADING", payload: false });
      return;
    }

    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const requestParams = {
        page: state.currentPage,
        limit: state.itemsPerPage,
        search: state.searchTerm,
        client_id: state.selectedClient?.id,
        start_date: state.dateRange.start,
        end_date: state.dateRange.end,
        warehouse_id: selectedWarehouse,
        company_id: selectedCompany,
        show_deleted: state.showDeleted,
        status: state.activeTab,
      };

      const result = await api.fetchProformas(requestParams);
      if (requestId !== proformasRequestRef.current) return;

      dispatch({
        type: "SET_PROFORMAS",
        payload: { proformas: result.proformas, total: result.total },
      });
    } catch (error) {
      if (requestId !== proformasRequestRef.current) return;
      console.error("Erreur lors du chargement des proformas:", error);
      message.error("Erreur lors du chargement des proformas");
      dispatch({
        type: "SET_ERROR",
        payload: "Erreur lors du chargement des proformas",
      });
    } finally {
      if (requestId === proformasRequestRef.current) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  }, [
    state.currentPage,
    state.itemsPerPage,
    state.searchTerm,
    state.selectedClient,
    state.dateRange,
    state.showDeleted,
    selectedWarehouse,
    selectedCompany,
    auth?.user?.id,
    state.activeTab,
  ]);

  useEffect(() => {
    refreshProformas();
    return () => { proformasRequestRef.current += 1; };
  }, [refreshProformas]);

  useEffect(() => {
    const loadInitialData = async () => {
      // Ne charger les données que si un magasin est sélectionné
      if (!currentWarehouse) {
        // Réinitialiser les données existantes si aucun magasin n'est sélectionné
        dispatch({ type: "SET_CLIENTS", payload: [] });
        dispatch({ type: "SET_PRODUITS", payload: [] });
        dispatch({
          type: "SET_PROFORMAS",
          payload: { proformas: [], total: 0 },
        });
        return;
      }

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const [clientsResponse, produitsResponse] = await Promise.all([
          api.fetchClients(selectedCompany?.id, currentWarehouse),
          api.fetchProduits({
            limit: 1000,
            warehouse: currentWarehouse,
          }),
        ]);

        dispatch({ type: "SET_CLIENTS", payload: clientsResponse });
        dispatch({ type: "SET_PRODUITS", payload: produitsResponse.products });
      } catch (error) {
        console.error("Erreur lors du chargement initial des données:", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Erreur lors du chargement des données initiales.",
        });
        message.error("Erreur lors du chargement des données.");
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadInitialData();
  }, [currentWarehouse]);

  const openProformaForm = () => {
    // Vérifier si l'utilisateur a la permission de créer un proforma
    if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.create")) {
      message.error(
        "Vous n'avez pas les droits nécessaires pour créer un proforma/devis."
      );
      return;
    }

    if (!selectedWarehouse) {
      Modal.warning({
        title: "Établissement non sélectionné",
        content:
          "Veuillez sélectionner un établissement avant de créer ou modifier une proforma.",
      });
      return;
    }

    // Réinitialiser les données du formulaire pour la création
    dispatch({ type: "RESET_FORM_DATA" });
    dispatch({ type: "TOGGLE_FORM", payload: true });
  };

  // Si l'utilisateur n'a pas la permission de voir ce module, afficher un message
  if (!hasPermission("Gestion Commerciale.Ventes.ProformaDevis.view")) {
    return <div>Accès non autorisé</div>;
  }

  // Si aucun magasin n'est sélectionné, afficher un message d'information
  if (!selectedWarehouse) {
    return (
      <ProformasContext.Provider value={{ state, dispatch, api }}>
        <Layout>
          <Layout.Content className="p-3 md:p-5">
            <Card>
              <div className="text-center py-16">
                <Alert
                  message="Établissement requis"
                  description="Veuillez sélectionner un établissement dans la barre de navigation pour accéder à la gestion des proformas."
                  type="info"
                  showIcon
                  style={{
                    maxWidth: 600,
                    margin: "0 auto",
                  }}
                />
              </div>
            </Card>
          </Layout.Content>
        </Layout>
      </ProformasContext.Provider>
    );
  }

  return (
    <ProformasContext.Provider value={{ state, dispatch, api }}>
      <Layout>
        <Layout.Content className="p-3 md:p-5">
          <Row
            justify="space-between"
            align="middle"
            className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4"
          >
            <Col className="w-full sm:w-auto">
              {/* N'afficher le bouton que si l'utilisateur a la permission de créer */}
              {hasPermission(
                "Gestion Commerciale.Ventes.ProformaDevis.create"
              ) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openProformaForm}
                >
                  Nouvelle proforma
                </Button>
              )}
            </Col>
            <Col className="w-full sm:w-auto sm:flex-grow">
              <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 w-full">
                <Input
                  value={state.searchTerm}
                  allowClear
                  placeholder="Rechercher un numéro de proforma ou un client"
                  prefix={<SearchOutlined />}
                  onChange={(e) =>
                    dispatch({
                      type: "SET_SEARCH",
                      payload: e.target.value,
                    })
                  }
                  className="w-full md:flex-1 md:min-w-[180px]"
                />
                <Select
                  placeholder="Client"
                  className="w-full md:flex-1 md:min-w-[180px]"
                  allowClear
                  onChange={(value: number) => {
                    const selectedClient = state.clients.find(
                      (c) => c.id === value
                    );
                    dispatch({
                      type: "SET_SELECTED_CLIENT",
                      payload: selectedClient,
                    });
                  }}
                >
                  {state.clients.map((client) => (
                    <Option key={client.id} value={client.id}>
                      {client.name ||
                        client.company_name ||
                        client.Nom_Raison_Sociale ||
                        "Client inconnu"}
                    </Option>
                  ))}
                </Select>
                <RangePicker
                  onChange={(dates, dateStrings) => {
                    if (dates) {
                      dispatch({
                        type: "SET_DATE_RANGE",
                        payload: {
                          start: dateStrings[0],
                          end: dateStrings[1],
                        },
                      });
                    } else {
                      dispatch({
                        type: "SET_DATE_RANGE",
                        payload: { start: "", end: "" },
                      });
                    }
                  }}
                />
                <Button
                  onClick={() =>
                    dispatch({
                      type: "TOGGLE_SHOW_DELETED",
                      payload: !state.showDeleted,
                    })
                  }
                  type={state.showDeleted ? "primary" : "default"}
                  className="w-full md:w-auto"
                >
                  {state.showDeleted
                    ? "Masquer supprimés"
                    : "Afficher supprimés"}
                </Button>
              </div>
            </Col>
          </Row>

          <Card>
            {/* Affichage du magasin sélectionné */}
            <div
              style={{
                marginBottom: 16,
                padding: 8,
                backgroundColor: "#f0f9ff",
                borderRadius: 4,
                border: "1px solid #bae6fd",
              }}
            >
              <Space>
                <span style={{ fontWeight: "bold", color: "#0369a1" }}>
                  Établissement sélectionné:
                </span>
                <span style={{ color: "#0c4a6e" }}>
                  {warehouseData?.name || `Établissement ID ${selectedWarehouse}`}
                </span>
              </Space>
            </div>
            <ProformasTable refreshProformas={refreshProformas} />
          </Card>
        </Layout.Content>
      </Layout>
    </ProformasContext.Provider>
  );
};

export default GestionProformas;
